import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { CookieSyncProvider } from "./contexts/CookieSyncContext";
import { useEffect, useRef } from "react";
import BottomNav from "./components/layout/BottomNav";
import FloatingAIBadge from "./components/FloatingAIBadge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Dashboard from "./pages/Dashboard";
import DailyDrop from "./pages/DailyDrop";
import DeepDives from "./pages/DeepDives";
import VideoVault from "./pages/VideoVault";
import KnowledgeCorner from "./pages/KnowledgeCorner";
import ContentDetail from "./pages/ContentDetail";
import Quiz from "./pages/Quiz";
import SmartDiscounts from "./pages/SmartDiscounts";
import Profile from "./pages/Profile";
import Leaderboard from "./pages/Leaderboard";
import Products from "./pages/Products";
import Admin from "./pages/Admin";
import Settings from "./pages/Settings";
import AIChat from "./pages/AIChat";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import NotFound from "./pages/NotFound";
import Sitemap from "./pages/Sitemap";

// Auth Callback Component
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.replace('#', '?')).get('session_id');

    if (sessionId) {
      fetch(`${BACKEND_URL}/api/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId })
      })
        .then(res => {
          if (!res.ok) throw new Error('Session exchange failed');
          return res.json();
        })
        .then(data => {
          if (data.user && data.token) {
            // Store token first - use try/catch for mobile browser compatibility
            try {
              localStorage.setItem('skinuse_token', data.token);
            } catch (e) {
              console.warn('localStorage not available:', e);
            }
            // Then update state
            login(data.user, data.token);
            // Longer delay for mobile devices to ensure state propagation
            setTimeout(() => {
              // Clear the hash first
              window.history.replaceState(null, '', window.location.pathname);
              // Use window.location for more reliable mobile redirect
              window.location.href = '/dashboard';
            }, 300);
          } else {
            throw new Error('Invalid session response');
          }
        })
        .catch(err => {
          console.error('Auth callback error:', err);
          window.location.href = '/login';
        });
    }
  }, [login, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-muted-foreground">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading, token } = useAuth();
  const location = useLocation();

  // Show loading only during initial auth check
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Check if we have a valid session - user state OR token in state OR token in localStorage
  let storedToken = null;
  try {
    storedToken = localStorage.getItem('skinuse_token');
  } catch (e) {
    // localStorage not available on some mobile browsers
  }
  const hasValidSession = user || token || storedToken;
  
  if (!hasValidSession) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  // Check for session_id in URL fragment SYNCHRONOUSLY during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  // Hide bottom nav on landing, login, register pages
  const hideBottomNav = ['/', '/login', '/register'].includes(location.pathname);

  return (
    <>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/daily-drop" element={<DailyDrop />} />
        <Route path="/deep-dives" element={<DeepDives />} />
        <Route path="/video-vault" element={<VideoVault />} />
        <Route path="/knowledge" element={<KnowledgeCorner />} />
        <Route path="/content/:id" element={<ContentDetail />} />
        <Route path="/quiz" element={<ProtectedRoute><Quiz /></ProtectedRoute>} />
        <Route path="/deals" element={<SmartDiscounts />} />
        <Route path="/products" element={<Products />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
        <Route path="/ai-advisor" element={<AIChat />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/sitemap" element={<Sitemap />} />
        {/* 404 Catch-all Route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      {!hideBottomNav && <BottomNav />}
      <FloatingAIBadge />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <CookieSyncProvider>
            <div className="App min-h-screen bg-background pb-16 lg:pb-0">
              <div className="grain-overlay" aria-hidden="true"></div>
              <AppRouter />
              <Toaster position="top-right" />
            </div>
          </CookieSyncProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
